import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DismissProfileScoreNotice } from 'api/profile';
import { DISMISS_SCORE_NOTICE } from 'store/actions';
import { Primary } from 'ui-components/Buttons';
import { getProgressFieldRoute } from 'utilities/index';
import { trackEvent } from 'utilities/mixpanel';
import { scoreNoticeView } from './scoreNoticeView';

const PURPLE = '#6c3bc2';
// Sits under the page header, on the right: beside Profile Progress and Need help.
const TOP = 92;

// Shown once on every page after an admin weights change lowered the profile %: why, and the best way back.
const ScoreChangeBanner = ({ startup_id }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const progress = useSelector((state) => state?.progress);
  const startup_name = useSelector((state) => state?.startupDetails?.startup_name);
  const { mutate: dismissOnServer } = DismissProfileScoreNotice();

  const view = scoreNoticeView(progress);

  if (!startup_id || !view) return null;

  // Hide at once; the server call keeps it closed on other devices too.
  const close = (action) => {
    dispatch({ type: DISMISS_SCORE_NOTICE });
    dismissOnServer({ startup_id });
    trackEvent('Profile - Score Change Notice', { startup_name, action, from: view.from, to: view.to });
  };

  const cta = view.action
    ? { text: view.action.label, route: getProgressFieldRoute(view.action.field) }
    : { text: 'Review my profile', route: '/profile' };

  const details = (
    <Box sx={{ p: '4px 2px' }}>
      <Typography sx={{ fontSize: '0.8rem', color: theme.palette.customColors.g200 }}>
        We&apos;ve changed the profile completion weightages. Your existing data is safe, and your score has been recalculated.
      </Typography>
      {view.changes.length > 0 && (
        <>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: theme.palette.customColors.g500, mt: '0.5rem' }}>What changed</Typography>
          <Box component="ul" sx={{ m: '0.25rem 0 0', pl: '1rem', fontSize: '0.8rem', color: theme.palette.customColors.g200 }}>
            {view.changes.map((line) => <li key={line}>{line}</li>)}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Box
        role="status"
        aria-label="Profile score updated"
        sx={{
          position: 'fixed',
          zIndex: 1200,
          top: TOP,
          right: { xs: 16, md: 24 },
          display: 'flex',
          maxWidth: 'calc(100vw - 32px)',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          alignItems: 'center',
          gap: '0.75rem',
          padding: '6px 6px 6px 16px',
          borderRadius: '12px',
          backgroundColor: '#F8F6FF',
          border: '1px solid #E4DEFB',
          boxShadow: '0 8px 20px rgba(16, 24, 40, 0.12)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: theme.palette.customColors.g500, whiteSpace: 'nowrap' }}>
            Your profile progress has been updated
          </Typography>
          <Tooltip
            title={details}
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={6000}
            componentsProps={{
              tooltip: { sx: { maxWidth: 320, backgroundColor: '#fff', border: '1px solid #EAECF0', borderRadius: '10px', boxShadow: '0 12px 24px rgba(16,24,40,.12)', p: '10px 12px' } },
              arrow: { sx: { color: '#fff', '&::before': { border: '1px solid #EAECF0' } } },
            }}
          >
            <IconButton size="small" aria-label="Why did my score change?" sx={{ p: '2px', color: theme.palette.customColors.g100 }}>
              <InfoOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ width: '1px', alignSelf: 'stretch', my: '6px', backgroundColor: '#E4DEFB', display: { xs: 'none', md: 'block' } }} />

        <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g100, whiteSpace: 'nowrap' }}>
          Score{' '}
          <Box component="span" sx={{ fontWeight: 700, color: theme.palette.customColors.g500 }}>{view.from}%</Box>
          {' → '}
          <Box component="span" sx={{ fontWeight: 700, color: PURPLE }}>{view.to}%</Box>
        </Typography>

        <Primary
          onClick={() => {
            close('cta');
            navigate(cta.route);
          }}
          sx={{ padding: '6px 14px', fontSize: '0.8rem', whiteSpace: 'nowrap', ml: { md: '0.25rem' } }}
        >
          {cta.text}
        </Primary>

        <IconButton size="small" aria-label="Dismiss" onClick={() => close('dismiss')} sx={{ color: theme.palette.customColors.g100 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
    </>
  );
};

export default ScoreChangeBanner;
